<template>
    <ClientOnly>
        <section v-if="shouldRender" class="MentoringDashboard">
            <div class="rounded-3xl border border-gray-200 bg-white p-4 sm:p-6 dark:border-default dark:bg-surface-card">
                <!-- Header — same on every page: title + public-profile + full-dashboard links -->
                <div class="flex flex-wrap items-center justify-between gap-3" :class="isCollapsed ? '' : 'mb-4'">
                    <!-- On the full page the parent already renders a big "Môj mentoring" heading -->
                    <h3 v-if="!fullPage" class="text-lg font-semibold text-gray-900 dark:text-white">Môj mentoring</h3>
                    <div v-else aria-hidden="true"></div>
                    <div class="flex flex-wrap items-center gap-4">
                        <button
                            v-if="isTopApproved"
                            type="button"
                            class="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-purple-100 px-3.5 py-1.5 text-sm font-semibold text-purple-700 shadow-sm transition-colors hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:bg-purple-900/50 dark:text-purple-300 dark:hover:bg-purple-900/70 dark:focus:ring-offset-gray-800"
                            @click="isHelpOpen = true"
                        >
                            <Icon name="heroicons:question-mark-circle" class="size-4" />
                            Pomoc
                        </button>
                        <NuxtLink
                            v-if="publicProfileHref"
                            :to="publicProfileHref"
                            target="_blank"
                            class="inline-flex items-center gap-1 text-sm font-medium text-purple-600 hover:underline dark:text-purple-400"
                        >
                            Verejný profil
                            <Icon name="heroicons:arrow-top-right-on-square" class="size-4" />
                        </NuxtLink>
                        <!-- Self-link — hidden on the full page (that IS this page) -->
                        <NuxtLink
                            v-if="!fullPage"
                            to="/profile/mentoring"
                            class="text-sm font-medium text-purple-600 hover:underline dark:text-purple-400"
                        >
                            Otvoriť celý prehľad →
                        </NuxtLink>
                        <!-- Shown on the full page too, but there it stays session-only (see onMounted/
                             watch guards): it starts expanded and does NOT share collapse state with the
                             homepage strip, so the dedicated page never opens collapsed. -->
                        <button
                            v-if="props.collapsible"
                            type="button"
                            class="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:text-txt-body dark:hover:bg-subtle dark:hover:text-white dark:focus:ring-offset-gray-800"
                            :aria-expanded="!isCollapsed"
                            aria-controls="mentoring-dashboard-content"
                            @click="toggleCollapsed"
                        >
                            <span>{{ isCollapsed ? 'Rozbaliť' : 'Zbaliť' }}</span>
                            <Icon
                                name="heroicons:chevron-up"
                                class="size-4 transition-transform"
                                :class="{ 'rotate-180': isCollapsed }"
                            />
                        </button>
                    </div>
                </div>

                <transition name="mentoring-dashboard-collapse">
                    <div v-if="!isCollapsed" id="mentoring-dashboard-content">
                        <!-- TOP-approved: full hub (stepper + action cards + payment tile) -->
                        <template v-if="isTopApproved">
                            <TopProcessStepper
                                :top-matched-count="buckets.matchedCount"
                                :top-accepted-count="buckets.acceptedCount"
                                :top-in-discussion-count="buckets.inDiscussionCount"
                                :top-requested-count="buckets.requestedCount"
                            />
                            <TopActionCards
                                v-if="buckets.loaded && buckets.actionableMentorings.length > 0"
                                :mentorings="buckets.actionableMentorings"
                                :user-id="store.userId"
                                @resolved="onResolved"
                            />

                            <!-- Running mentorings (officially started) — one card per pair, each deep-links to its detail page -->
                            <div
                                v-if="buckets.loaded && buckets.runningMentorings.length > 0"
                                class="mb-6 flex flex-col gap-3"
                            >
                                <NuxtLink
                                    v-for="m in buckets.runningMentorings"
                                    :key="m.id"
                                    :to="`/profile/activity/mentoring/${m.id}`"
                                    class="group flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-4 transition-colors hover:border-purple-300 hover:bg-purple-50/30 dark:border-default dark:bg-surface-card dark:hover:border-purple-700 dark:hover:bg-purple-900/20 sm:flex-row sm:items-center sm:justify-between sm:p-5"
                                >
                                    <div class="flex items-start gap-3">
                                        <div
                                            class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-purple-100 text-base font-bold text-purple-700 dark:bg-purple-900/50 dark:text-purple-300"
                                            aria-hidden="true"
                                        >
                                            {{ partnerInitial(m) }}
                                        </div>
                                        <div class="flex-1">
                                            <p class="font-semibold text-gray-900 dark:text-white">
                                                Mentoring s {{ partnerName(m) }}
                                            </p>
                                            <p class="mt-0.5 text-sm text-gray-600 dark:text-txt-body">
                                                Otvor priestor pre vašu spoluprácu — stretnutia, ciele, progres.
                                            </p>
                                        </div>
                                    </div>
                                    <span
                                        class="inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors group-hover:border-purple-400 group-hover:text-purple-700 group-hover:dark:text-purple-300 dark:border-default dark:bg-subtle dark:text-txt-body sm:self-auto"
                                    >
                                        <Icon name="heroicons:arrow-right" class="size-4" />
                                        Otvoriť
                                    </span>
                                </NuxtLink>
                            </div>
                        </template>

                        <!-- Non-TOP with actionable mentorings: action cards only -->
                        <template v-else-if="actionableAllTypes.length > 0">
                            <TopActionCards
                                :mentorings="actionableAllTypes"
                                :user-id="store.userId"
                                @resolved="onResolved"
                            />
                        </template>
                    </div>
                </transition>
            </div>

            <MentoringHelpModal v-if="isTopApproved" v-model="isHelpOpen" />
        </section>
    </ClientOnly>
</template>

<script setup lang="ts">
import type { UserProfile } from '#shared/types/User'
import { VisibilityType } from '#shared/types/visibility'
import TopProcessStepper from '@/components/mentoring/TopProcessStepper.vue'
import TopActionCards from '@/components/mentoring/TopActionCards.vue'
import MentoringHelpModal from '@/components/mentoring/MentoringHelpModal.vue'
import { useTopMentoringNextAction } from '@/composables/mentoring/useTopMentoringNextAction'

const props = withDefaults(
    defineProps<{
        collapsible?: boolean
        collapsedStorageKey?: string
        defaultCollapsed?: boolean
        /** Rendered as the whole /profile/mentoring page: no self-link, no title, never collapsed */
        fullPage?: boolean
    }>(),
    {
        collapsible: true,
        collapsedStorageKey: 'growni-mentoring-dashboard-collapsed',
        defaultCollapsed: false,
        fullPage: false
    }
)

const store = useApplicationStore()
const userProfile = computed<UserProfile | null>(() => store.userProfile as UserProfile | null)
const isCollapsed = ref(props.defaultCollapsed)
const isHelpOpen = ref(false)

const { ensureLoaded, isTopApproved, buckets, actionableAllTypes, applyResolution } =
    useTopMentoringNextAction()

// Keep the shared mentoring cache in sync when a card action resolves, so the change is
// reflected on every surface (this strip, /mentori, /profile/mentoring) without a refetch.
const onResolved = (payload: { id: string; action: 'start' | 'reject' | 'archive' }) =>
    applyResolution(payload.id, payload.action)

onMounted(() => {
    // Never restore a collapsed state on the full page — there is no toggle to reopen it
    if (props.collapsible && !props.fullPage) {
        const savedCollapsed = localStorage.getItem(props.collapsedStorageKey)
        if (savedCollapsed !== null) {
            isCollapsed.value = savedCollapsed === 'true'
        }
    }
    if (store.userId) ensureLoaded()
})

watch(isCollapsed, (collapsed) => {
    if (props.collapsible && !props.fullPage && import.meta.client) {
        localStorage.setItem(props.collapsedStorageKey, String(collapsed))
    }
})

const toggleCollapsed = () => {
    isCollapsed.value = !isCollapsed.value
}

// Public-profile link target — prefer mentor side (most users), fall back to mentee side.
// Hidden if the user has no public profile at all.
const publicProfileHref = computed(() => {
    const slug = userProfile.value?.slug
    if (!slug) return null
    const hasMentor =
        userProfile.value?.mentorTopApproved ||
        userProfile.value?.mentorProfile?.visibility === VisibilityType.published
    if (hasMentor) return `/mentor/${slug}`
    const hasMentee =
        userProfile.value?.menteeTopApproved ||
        userProfile.value?.menteeProfile?.visibility === VisibilityType.published
    if (hasMentee) return `/mentees/${slug}`
    return null
})

// Render rule: anonymous → never; non-TOP with no actionable mentorings → never; else → render.
const shouldRender = computed(() => {
    if (!store.userId) return false
    if (isTopApproved.value) return true
    return actionableAllTypes.value.length > 0
})

// Helpers for running mentoring cards — same logic as TopActionCards initials/name.
const partnerName = (m: {
    mentorId?: string
    mentorMeta?: { firstName?: string; lastName?: string }
    menteeMeta?: { firstName?: string; lastName?: string }
}) => {
    const userIsMentor = m.mentorId === store.userId
    const meta = userIsMentor ? m.menteeMeta : m.mentorMeta
    const name = `${meta?.firstName ?? ''} ${meta?.lastName ?? ''}`.trim()
    return name || 'tvoj partner'
}
const partnerInitial = (m: {
    mentorId?: string
    mentorMeta?: { firstName?: string; lastName?: string }
    menteeMeta?: { firstName?: string; lastName?: string }
}) => {
    const userIsMentor = m.mentorId === store.userId
    const meta = userIsMentor ? m.menteeMeta : m.mentorMeta
    const f = meta?.firstName?.trim()?.[0]
    const l = meta?.lastName?.trim()?.[0]
    return ((f ?? '') + (l ?? '')).toUpperCase() || '?'
}
</script>

<style scoped>
.mentoring-dashboard-collapse-enter-active,
.mentoring-dashboard-collapse-leave-active {
    transition:
        opacity 0.18s ease,
        transform 0.18s ease;
}

.mentoring-dashboard-collapse-enter-from,
.mentoring-dashboard-collapse-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}
</style>
